import { useBaseInputV2AdornmentContext } from "./useBaseInputV2AdornmentContext.ts";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import {
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
} from "react";

export type AdornmentPosition = "start" | "end";

export type BaseInputV2AdornmentProps = HTMLAttributes<HTMLDivElement> & {
  isAction?: boolean;
};

export const BaseInputV2Adornment = forwardRef(
  (
    props: PropsWithChildren<BaseInputV2AdornmentProps>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { isAction = false, ...restProps } = props;
    const { disabled, position } = useBaseInputV2AdornmentContext();
    return (
      <div
        ref={ref}
        css={rootStyles({ position, disabled, isAction })}
        {...restProps}
      />
    );
  },
);

const rootStyles =
  ({
    position,
    disabled,
    isAction,
  }: {
    position: AdornmentPosition;
    disabled?: boolean;
    isAction: boolean;
  }) =>
  (theme: ExactTheme) => {
    return [
      css`
        position: relative;
        display: grid;
        place-content: center;
        height: 100%;
        max-width: 34px;
        flex: 0 0 34px;

        color: ${disabled
          ? theme.inputColorV2.disabled
          : theme.colorsV2.grey[600]};

        :before,
        :after {
          position: absolute;
          content: none;
          width: 1px;
          top: 25%;
          height: 50%;
          background-color: ${theme.colorsV2.grey[200]};
        }
        :before {
          left: 0;
        }
        :after {
          right: 0;
        }
      `,
      position === "start" &&
        css`
          :after {
            content: "";
          }
          border-bottom-left-radius: ${theme.borderRadiusV2.primary};
          border-top-left-radius: ${theme.borderRadiusV2.primary};
        `,
      position === "end" &&
        css`
          :before {
            content: "";
          }
          border-top-right-radius: ${theme.borderRadiusV2.primary};
          border-bottom-right-radius: ${theme.borderRadiusV2.primary};
        `,
      isAction &&
        css`
          cursor: pointer;
          &:hover {
            background-color: ${theme.colorsV2.grey[50]};
            :before,
            :after {
              content: none;
            }
          }

          &:active {
            background-color: ${theme.colorsV2.grey[100]};
          }
        `,
    ];
  };
